import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';

export interface SupplierOrderMessage {
    _id: string;
    partstraderCommentId?: number;
    comment: string;
    fromSupplier: boolean;
    senderName: string | null;
    sentByUserId?: string;
    sentByUserName?: string;
    timestamp: string;
}

interface SupplierOrderMessagesResponse {
    messages?: Array<SupplierOrderMessage>;
}

interface GetSupplierOrderCommentsInput {
    supplierOrderId: string;
    forceRefresh?: boolean;
}

const getSupplierOrderComments = async (
    sessionId: string,
    input: GetSupplierOrderCommentsInput,
): Promise<Array<SupplierOrderMessage>> => {
    const { data } = await axios.post<SupplierOrderMessagesResponse>(
        `${API_URL}/order/supplier/comments`,
        {
            supplierOrderId: input.supplierOrderId,
            forceRefresh: input.forceRefresh === true,
        },
        {
            headers: {
                Authorization: sessionId,
            },
        },
    );
    return data.messages ?? [];
};

export const useGetSupplierOrderComments = () => {
    const sessionContext = useContext(SessionContext);

    return useMutation({
        mutationFn: (input: GetSupplierOrderCommentsInput) => {
            if (!sessionContext?.sessionId) throw new Error('Not authenticated');
            return getSupplierOrderComments(sessionContext.sessionId, input);
        },
    });
};

interface AddSupplierOrderCommentInput {
    supplierOrderId: string;
    comment: string;
}

const addSupplierOrderComment = async (
    sessionId: string,
    input: AddSupplierOrderCommentInput,
): Promise<void> => {
    await axios.post(
        `${API_URL}/order/supplier/add-comment`,
        input,
        {
            headers: {
                Authorization: sessionId,
            },
        },
    );
};

export const useAddSupplierOrderComment = () => {
    const sessionContext = useContext(SessionContext);

    return useMutation({
        mutationFn: (input: AddSupplierOrderCommentInput) => {
            if (!sessionContext?.sessionId) throw new Error('Not authenticated');
            return addSupplierOrderComment(sessionContext.sessionId, input);
        },
    });
};
